import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useTranslation } from './TranslationContext';
import { getShippingDominantRate } from '../utils/shippingVat';

const CartContext = createContext(null);
const API_URL = process.env.REACT_APP_API_URL;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [couponDiscounts, setCouponDiscounts] = useState([]);
  const [ruleDiscount, setRuleDiscount] = useState(0);
  const [ruleDiscountDescriptor, setRuleDiscountDescriptor] = useState(null);
  const [freeItems, setFreeItems] = useState([]);
  const [availableOffers, setAvailableOffers] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingFree, setShippingFree] = useState(false);
  const [volumeDiscount, setVolumeDiscount] = useState(0);
  const [volumeDiscountPercentage, setVolumeDiscountPercentage] = useState(null);
  const [appliedBalance, setAppliedBalance] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [itemVat, setItemVat] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [totalBeforeRounding, setTotalBeforeRounding] = useState(0);
  const [roundedTotal, setRoundedTotal] = useState(0);
  const [roundOffDiff, setRoundOffDiff] = useState(0);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [netItemsTotal, setNetItemsTotal] = useState(0);
  const [itemsRoundOffDiff, setItemsRoundOffDiff] = useState(0);
  const [billingStatus, setBillingStatus] = useState(null);
  const [vatExclusive, setVatExclusive] = useState(false);
  const [netSubtotal, setNetSubtotal] = useState(0);
  const [netShipping, setNetShipping] = useState(0);
  const [vatOnDiscount, setVatOnDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { langCode } = useTranslation();

  // Memoized so a language toggle re-fires a fetch with the new lang, while
  // still letting useEffect's dep comparison handle auth-state changes correctly.
  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/cart?lang=${encodeURIComponent(langCode)}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
        setCouponDiscounts(data.couponDiscounts || []);
        setRuleDiscount(data.ruleDiscount || 0);
        setRuleDiscountDescriptor(data.ruleDiscountDescriptor || null);
        setFreeItems(data.freeItems || []);
        setAvailableOffers(data.availableOffers || []);
        setShippingCost(data.shippingCost ?? 0);
        setShippingFree(data.shippingFree ?? false);
        setVolumeDiscount(data.volumeDiscount ?? 0);
        setVolumeDiscountPercentage(data.volumeDiscountPercentage ?? null);
        setAppliedBalance(data.appliedBalance ?? 0);
        setVatAmount(data.vatAmount ?? 0);
        setItemVat(data.itemVat ?? 0);
        setSubtotal(data.subtotal ?? 0);
        setTotalBeforeRounding(data.totalBeforeRounding ?? 0);
        setRoundedTotal(data.roundedTotal ?? 0);
        setRoundOffDiff(data.roundOffDiff ?? 0);
        setItemsTotal(data.itemsTotal ?? 0);
        setNetItemsTotal(data.netItemsTotal ?? 0);
        setItemsRoundOffDiff(data.itemsRoundOffDiff ?? 0);
        setBillingStatus(data.billingStatus ?? null);
        setVatExclusive(data.vatExclusive ?? false);
        setNetSubtotal(data.netSubtotal ?? 0);
        setNetShipping(data.netShipping ?? 0);
        setVatOnDiscount(data.vatOnDiscount ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [langCode]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCartItems([]);
      setCouponDiscounts([]);
      setRuleDiscount(0);
      setRuleDiscountDescriptor(null);
      setFreeItems([]);
      setAvailableOffers([]);
      setShippingCost(0);
      setShippingFree(false);
      setVolumeDiscount(0);
      setVolumeDiscountPercentage(null);
      setAppliedBalance(0);
      setVatAmount(0);
      setItemVat(0);
      setSubtotal(0);
      setTotalBeforeRounding(0);
      setRoundedTotal(0);
      setRoundOffDiff(0);
      setItemsTotal(0);
      setItemsRoundOffDiff(0);
      setBillingStatus(null);
      setVatExclusive(false);
      setNetSubtotal(0);
      setNetShipping(0);
      setVatOnDiscount(0);
    }
  }, [isAuthenticated, fetchCart]);

  const addToCart = async (product, quantity = 1) => {
    try {
      if (!product?.sku) {
        throw new Error('Cannot add to cart: missing variant SKU');
      }
      const response = await fetch(`${API_URL}/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sku: product.sku,
          quantity
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add to cart');
      }

      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      const response = await fetch(`${API_URL}/cart/${cartItemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchCart();
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(cartItemId);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/cart/${cartItemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update cart');
      }

      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      const response = await fetch(`${API_URL}/cart`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const getCartTotal = () => {
    if (cartItems.length === 0) return 0;
    // The cart page shows the API's items-only payable (gross, balance-adjusted,
    // excluding the hidden shipping leg). Mirror it in the badge when known;
    // otherwise derive gross items − grossed-up discounts locally.
    if (itemsTotal > 0) return itemsTotal;
    const grossTotal = cartItems.reduce((total, item) => {
      const price = Number(item.price) || 0;
      return total + (price * item.quantity);
    }, 0);
    const dominantRate = getShippingDominantRate(cartItems.map(it => ({
      net: (Number(it.netPrice) || Number(it.price) || 0) * (Number(it.quantity) || 1),
      vat_rate: Number(it.vatRate) || Number(it.vat_rate) || 0,
    })));
    const gf = !vatExclusive && dominantRate > 0 ? 1 + dominantRate / 100 : 1;
    const gross = (net) => Math.round((Number(net) || 0) * gf * 100) / 100;
    const totalCouponDiscount = couponDiscounts.reduce((s, c) => s + gross(Number(c.computed_discount) || 0), 0);
    return Math.max(0, grossTotal - totalCouponDiscount - gross(ruleDiscount));
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        couponDiscounts,
        ruleDiscount,
        ruleDiscountDescriptor,
        freeItems,
        availableOffers,
        shippingCost,
        shippingFree,
        volumeDiscount,
        volumeDiscountPercentage,
        appliedBalance,
        vatAmount,
        itemVat,
        subtotal,
        totalBeforeRounding,
        roundedTotal,
        roundOffDiff,
        itemsTotal,
        netItemsTotal,
        itemsRoundOffDiff,
        billingStatus,
        vatExclusive,
        netSubtotal,
        netShipping,
        vatOnDiscount,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        refreshCart: fetchCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};